import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { ordersApi, wishlistApi, tradeInApi, accountApi } from '../lib/api';
import { User, Package, Heart, Recycle, LogOut, ChevronRight, ShieldCheck, Download, Trash2, Shirt } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export default function AccountPage() {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [tradeIns, setTradeIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExportData = async () => {
    try {
      const res = await accountApi.exportData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `biolosophy-miei-dati-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: t({ it: 'Dati scaricati', en: 'Data downloaded' }) });
    } catch (error) {
      toast({ title: t({ it: 'Errore durante l\'esportazione', en: 'Export error' }), variant: 'destructive' });
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await accountApi.deleteAccount();
      toast({ title: t({ it: 'Account eliminato. Ci mancherai!', en: 'Account deleted. We will miss you!' }) });
      await logout();
      navigate('/');
    } catch (error) {
      toast({ title: t({ it: 'Errore durante l\'eliminazione', en: 'Deletion error' }), variant: 'destructive' });
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, wishlistRes, tradeInsRes] = await Promise.all([
        ordersApi.getAll(),
        wishlistApi.get(),
        tradeInApi.getHistory(),
      ]);
      setOrders(ordersRes.data.orders || []);
      setWishlist(wishlistRes.data.items || []);
      setTradeIns(tradeInsRes.data.trade_ins || []);
    } catch (error) {
      console.error('Error fetching account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast({ title: 'Logout effettuato' });
    navigate('/');
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#4A5D23] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isMainAccount = location.pathname === '/account';

  const menuItems = [
    { path: '/account/orders', label: t({ it: 'I Miei Ordini', en: 'My Orders' }), icon: Package, count: orders.length },
    { path: '/guardaroba', label: t({ it: 'Il Mio Guardaroba', en: 'My Wardrobe' }), icon: Shirt, count: null },
    { path: '/account/wishlist', label: 'Wishlist', icon: Heart, count: wishlist.length },
    { path: '/account/trade-in', label: 'Biolosophy Value', icon: Recycle, count: tradeIns.length },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-8">
          {t({ it: 'Il Mio Account', en: 'My Account' })}
        </h1>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[#F2EFE9] p-6 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 flex items-center justify-center bg-[#4A5D23] text-white rounded-full">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-[#57534E]">{user?.email}</p>
                </div>
              </div>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  data-testid="admin-panel-link"
                  className="block w-full text-center py-2 bg-[#C05746] text-white text-sm hover:bg-[#a84637] transition-colors"
                >
                  Pannello Admin
                </Link>
              )}
            </div>

            <nav className="space-y-2">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`account-nav-${item.path.split('/').pop()}`}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    location.pathname === item.path
                      ? 'bg-[#1C1917] text-white'
                      : 'bg-[#F2EFE9] hover:bg-[#E5E0D8]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.count > 0 && (
                      <span className={`text-sm px-2 py-0.5 rounded-full ${
                        location.pathname === item.path
                          ? 'bg-white/20'
                          : 'bg-[#E5E0D8]'
                      }`}>
                        {item.count}
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full flex items-center gap-3 p-4 bg-[#F2EFE9] hover:bg-red-50 text-[#C05746] transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>{t({ it: 'Esci', en: 'Sign Out' })}</span>
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {isMainAccount ? (
              <div className="space-y-8">
                {/* Recent Orders */}
                <div className="bg-[#F2EFE9] p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-medium">{t({ it: 'Ordini Recenti', en: 'Recent Orders' })}</h2>
                    <Link to="/account/orders" className="text-[#4A5D23] text-sm hover:underline">
                      {t({ it: 'Vedi tutti', en: 'View all' })}
                    </Link>
                  </div>
                  {loading ? (
                    <div className="animate-pulse space-y-3">
                      {[1, 2].map((i) => (
                        <div key={i} className="h-16 bg-[#E5E0D8]" />
                      ))}
                    </div>
                  ) : orders.length === 0 ? (
                    <p className="text-[#57534E]">{t({ it: 'Nessun ordine ancora', en: 'No orders yet' })}</p>
                  ) : (
                    <div className="space-y-3">
                      {orders.slice(0, 3).map((order) => (
                        <Link
                          key={order.order_id}
                          to={`/account/orders/${order.order_id}`}
                          className="block p-4 bg-white hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between">
                            <div>
                              <p className="font-medium">{order.order_id}</p>
                              <p className="text-sm text-[#57534E]">
                                {new Date(order.created_at).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">€{order.total?.toFixed(2)}</p>
                              <p className={`text-sm capitalize ${
                                order.status === 'delivered' ? 'text-[#4A5D23]' :
                                order.status === 'cancelled' ? 'text-[#C05746]' :
                                'text-[#57534E]'
                              }`}>
                              {t({
                                delivered: { it: 'Consegnato', en: 'Delivered' },
                                confirmed: { it: 'Confermato', en: 'Confirmed' },
                                pending: { it: 'In attesa', en: 'Pending' },
                                cancelled: { it: 'Annullato', en: 'Cancelled' },
                                shipped: { it: 'Spedito', en: 'Shipped' },
                              }[order.status] || { it: order.status, en: order.status })}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Wishlist Preview */}
                <div className="bg-[#F2EFE9] p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-medium">Wishlist</h2>
                    <Link to="/account/wishlist" className="text-[#4A5D23] text-sm hover:underline">
                      {t({ it: 'Vedi tutti', en: 'View all' })}
                    </Link>
                  </div>
                  {loading ? (
                    <div className="animate-pulse flex gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-20 h-24 bg-[#E5E0D8]" />
                      ))}
                    </div>
                  ) : wishlist.length === 0 ? (
                    <p className="text-[#57534E]">{t({ it: 'La tua wishlist è vuota', en: 'Your wishlist is empty' })}</p>
                  ) : (
                    <div className="flex gap-4 overflow-x-auto pb-2">
                      {wishlist.slice(0, 4).map((product) => (
                        <Link
                          key={product.product_id}
                          to={`/product/${product.product_id}`}
                          className="shrink-0 w-20"
                        >
                          <img
                            src={product.images?.[0] || 'https://via.placeholder.com/80x100'}
                            alt={product.name}
                            className="w-20 h-24 object-cover"
                          />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Trade-In CTA */}
                <div className="bg-[#4A5D23] text-white p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <Recycle className="w-8 h-8" />
                    <h2 className="text-xl font-medium">Biolosophy Value</h2>
                  </div>
                  <p className="text-white/80 mb-4">
                    {t({
                      it: 'Hai capi Biolosophy da restituire? Scopri quanto puoi risparmiare sul prossimo acquisto!',
                      en: 'Do you have Biolosophy garments to bring back? Find out how much you can save on your next purchase!',
                    })}
                  </p>
                  <Link
                    to="/account/trade-in"
                    data-testid="account-trade-in-cta"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#4A5D23] font-medium hover:bg-[#F2EFE9] transition-colors"
                  >
                    {t({ it: 'Scopri i tuoi sconti', en: 'Discover your discounts' })}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                {/* Privacy & Data (GDPR) */}
                <div className="bg-[#F2EFE9] p-6" data-testid="privacy-data-section">
                  <div className="flex items-center gap-3 mb-4">
                    <ShieldCheck className="w-6 h-6 text-[#4A5D23]" />
                    <h2 className="text-xl font-medium">{t({ it: 'Privacy e Dati', en: 'Privacy & Data' })}</h2>
                  </div>
                  <p className="text-sm text-[#57534E] mb-4">
                    {t({
                      it: 'In conformità al GDPR puoi scaricare una copia dei tuoi dati personali o eliminare definitivamente il tuo account.',
                      en: 'In compliance with GDPR you can download a copy of your personal data or permanently delete your account.',
                    })}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleExportData}
                      data-testid="export-data-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E0D8] hover:border-[#1C1917] text-sm transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      {t({ it: 'Scarica i miei dati', en: 'Download my data' })}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(true)}
                      data-testid="delete-account-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-white border border-[#C05746] text-[#C05746] hover:bg-[#C05746] hover:text-white text-sm transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t({ it: 'Elimina il mio account', en: 'Delete my account' })}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Outlet context={{ orders, wishlist, tradeIns, loading, refreshData: fetchData }} />
            )}
          </div>
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="delete-account-modal">
          <div className="bg-white max-w-md w-full p-6">
            <h3 className="text-lg font-medium mb-2 text-[#C05746]">
              {t({ it: 'Eliminare definitivamente l\'account?', en: 'Permanently delete your account?' })}
            </h3>
            <p className="text-sm text-[#57534E] mb-4">
              {t({
                it: 'Questa azione è irreversibile: profilo, wishlist, carrello e notifiche verranno eliminati. Gli ordini saranno resi anonimi e conservati solo per obblighi fiscali. Scrivi ELIMINA per confermare.',
                en: 'This action is irreversible: profile, wishlist, cart and notifications will be deleted. Orders will be anonymized and kept only for tax obligations. Type DELETE to confirm.',
              })}
            </p>
            <input
              type="text"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder={t({ it: 'ELIMINA', en: 'DELETE' })}
              data-testid="delete-confirm-input"
              className="w-full border border-[#E5E0D8] p-3 text-sm mb-4 focus:outline-none focus:border-[#C05746]"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setConfirmDelete(false); setDeleteText(''); }}
                data-testid="delete-cancel-btn"
                className="px-4 py-2 text-sm bg-[#F2EFE9] hover:bg-[#E5E0D8] transition-colors"
              >
                {t({ it: 'Annulla', en: 'Cancel' })}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting || (deleteText !== 'ELIMINA' && deleteText !== 'DELETE')}
                data-testid="delete-confirm-btn"
                className="px-4 py-2 text-sm bg-[#C05746] text-white hover:bg-[#a84637] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting
                  ? t({ it: 'Eliminazione...', en: 'Deleting...' })
                  : t({ it: 'Elimina definitivamente', en: 'Delete permanently' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
